"use client";

import { ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";
import { memo, useTransition } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/feature/auth/lib/auth-error";
import { useLazyGetFlightRevalidateQuery } from "@/feature/flight/checkout/api/revalidate";
import { encodeFlightId } from "@/feature/flight/search/lib/flight-id-codec";
import { useAppSelector } from "@/redux/hooks";

function PureFlightRevalidateButton({
  flight_id,
  searchId,
  disableResponseCheck,
}: {
  flight_id: string;
  searchId: string;
  flightTabRef?: React.RefObject<HTMLDivElement | null>;
  disableResponseCheck?: boolean;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { isResponseEnd } = useAppSelector((state) => state.flightSearchStatus);

  const [triggerRevalidate, { isFetching: isRevalidating }] =
    useLazyGetFlightRevalidateQuery();

  const isDisabled =
    (!disableResponseCheck && !isResponseEnd) ||
    isPending ||
    !searchId ||
    isRevalidating;

  const handleClick = async () => {
    if (isDisabled) {
      return;
    }

    try {
      const res = await triggerRevalidate(
        {
          flightId: flight_id,
          searchId,
        },
        true,
      ).unwrap();

      if (!res?.data) {
        toast.error("Unable to revalidate this flight right now.");
        return;
      }
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          "Unable to revalidate this flight right now.",
        ),
      );
      return;
    }

    const encodedFlightId = encodeFlightId(flight_id);
    const encodedSearchId = encodeFlightId(searchId);
    const nextPath = `/flights/${encodedFlightId}?searchId=${encodedSearchId}`;
    startTransition(() => {
      router.push(nextPath);
    });
  };

  return (
    <Button
      size="sm"
      disabled={isDisabled}
      onClick={(event) => {
        event.stopPropagation();
        void handleClick();
      }}
      className="h-8 rounded-full"
    >
      {isRevalidating || isPending ? "Opening..." : "book"}
      <ChevronRight className="size-4" />
    </Button>
  );
}

export default memo(PureFlightRevalidateButton);
